// @ts-check

import { Select } from 'antd';
import { get_initial_data, get_locations } from '~/api/zero-api';
import { useZeroContext } from '~/components/ZeroContext';
import useZeroSelector from '~/hooks/useZeroSelector';
import { keyBy } from 'lodash';
import { isPublicUser } from '~/other/Helper';
import { useCallback, useEffect, useState } from 'react';


let locationCache = null;


function filterLocations(locations, filter) {
    let filteredLocations = [...locations];

    if (filter) {
        const lowerFilter = filter.toLowerCase();

        filteredLocations = filteredLocations.filter(location => {
            const name = location.name.toLowerCase();
            return name.includes(lowerFilter);
        });
    }

    return filteredLocations;
}


function convertLocationToOption(location) {
    return {
        value: location.uuid,
        label: location.name
    };
}


function generateOptions(locations, filter) {
    const filteredLocations = filterLocations(locations, filter);
    return filteredLocations.map(convertLocationToOption);
}


async function fetchLocations(user, kv) {
    try {
        if (isPublicUser(user)) {
            const locations = kv.get('locations');
            return locations ?? [];
        } else {
            const res = await get_locations('?for_forms=1');
            const content = await res.json();
            return content ?? [];
        }
    } catch (err) {
        console.error('Could not fetch locations:', err);
        return [];
    }
}


async function loadLocationsFromCache(user, kv) {
    if (locationCache && (Date.now() - locationCache.timestamp < 300)) {
        return locationCache.data;
    } else if (locationCache?.fetchInProgress) {
        return await locationCache.fetchInProgress;
    } else {
        const fetchInProgress = new Promise((resolve) => {
            fetchLocations(user, kv).then(resolve);
        });
        locationCache = {
            fetchInProgress
        };

        const locations = await fetchInProgress;
        return locations;
    }
}


export default function LocationDropdown({selectedLocation, onChange, disabled}) {
    const { kv } = useZeroContext();
    const user = useZeroSelector(state => state.user.user);
    const [filter, setFilter] = useState('');
    const [options, setOptions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [locationMap, setLocationMap] = useState({});

    // fetch locations (or load from cache) on initial render
    // add any missing locations already saved to field to top of locations
    useEffect(() => {
        if (!disabled) {
            loadLocationsFromCache(user, kv).then(newLocations => {
                const newLocationsCopy = [...newLocations];

                if (selectedLocation) {
                    const found = newLocationsCopy.find(dirLocation => dirLocation.uuid === selectedLocation.uuid);
                    if (!found) {
                        newLocationsCopy.splice(0, 0, selectedLocation);
                    }
                }

                setLocations(newLocationsCopy);
            });
        }
    }, [disabled, user, kv]);

    // update options when filter changes
    useEffect(() => {
        setOptions(generateOptions(locations, filter));
    }, [filter, locations]);

    // build location map from locations
    useEffect(() => {
        setLocationMap(keyBy(locations, 'uuid'));
    }, [locations]);

    const locationChangedCallback = useCallback((value) => {
        if (value) {
            onChange(locationMap[value]);
        } else {
            onChange(null);
        }
    }, [locationMap, onChange]);

    return (
        <div>
            <Select
                style={{width: '100%'}}
                placeholder="Select a location..."
                options={options}
                loading={!disabled && locations.length === 0}
                disabled={disabled}
                virtual={false}
                dropdownStyle={{zIndex: '1200'}}
                onChange={locationChangedCallback}
                value={selectedLocation ? selectedLocation.uuid : undefined}
                showSearch={true}
                filterOption={false}
                onSearch={setFilter}
                onDropdownVisibleChange={visible => {
                    !visible && setFilter('');
                }}
                allowClear={true}
            />
        </div>
    );
}
